// ============================================
// PromotionTesterModal
// Thử engine khuyến mãi trên một giỏ mẫu (sheet 2 — mục #41)
// ============================================
//
// BE xác nhận 04/09/2026: KHÔNG cần endpoint preview riêng — POST /orders/verify/
// vốn đã nhận giỏ mẫu và trả `applicable_promotions` kèm subtotal /
// total_discount / estimated_total. Read-only, có gác scope theo cửa hàng nên
// không tạo đơn và không đụng tồn.
//
// Vì sao cần màn này: logic tính giảm giá chạy ở BE cho POS và user-app, còn
// admin thì không có đường nào kiểm tra một khuyến mãi TRƯỚC KHI bật nó. Bật
// nhầm một khuyến mãi ở cửa hàng thật là mất tiền thật.
//
// Kết quả rỗng KHÔNG phải lỗi: nghĩa là không khuyến mãi nào khớp giỏ này —
// engine đã tính cả điều kiện ngày giờ, cửa hàng và hạng khách. Màn hình nói
// rõ điều đó thay vì để trống.

import { useEffect, useMemo, useState } from 'react';
import type { ApplicablePromotion } from '@conai/shared-types';
import { useI18n } from '../../../i18n';
import { useVerifyCart, useStores, useProducts, usePromotions } from '../../../hooks';
import { useMoney } from '../../../providers/CurrencyProvider';
import { SelectDropdown } from '../../../components/shared';

interface CartLine {
  productId: string;
  quantity: string;
}

interface PromotionTesterModalProps {
  defaultStoreId?: string;
  onClose: () => void;
}

/**
 * Dòng khuyến mãi này có phải loại PHẢI NHẬP MÃ không (bug #250).
 *
 * BE đã trả cờ thật từ 18/09/2026: `requires_code` và `source` ("AUTO"|"CODE").
 * Bản trước phải SUY ĐOÁN từ việc có `code` và khớp mã người dùng gửi — đoán
 * sai thì gắn nhầm nhãn. Giờ đọc thẳng.
 *
 * Nhánh suy đoán giữ lại làm dự phòng vì bản BE này còn ở nhánh local, chưa
 * lên QA — bỏ hẳn bây giờ thì mọi dòng trên QA đều rơi về nhãn "Tự động".
 * Gỡ sau khi BE lên QA.
 */
function isCodePromotion(
  /**
   * Nhận thẳng `ApplicablePromotion`, KHÔNG khai một object-type riêng.
   *
   * Object-type mà mọi field đều optional là "weak type" của TS: nó chỉ nhận
   * đối số có ÍT NHẤT MỘT field trùng tên. Nên mỗi lần `shared-types` lệch một
   * nhịp so với FE — vd `dist/` chưa build lại — thì chỗ này nổ
   * TS2559 "has no properties in common", một thông báo chẳng liên quan gì tới
   * nguyên nhân thật. Nhận đúng kiểu thì lỗi (nếu có) chỉ ra đúng field thiếu.
   */
  promo: ApplicablePromotion,
  usedCode: string | null
): boolean {
  if (typeof promo.requires_code === 'boolean') return promo.requires_code;
  if (promo.source) return promo.source === 'CODE';
  // ↓ dự phòng cho QA chưa có cờ
  if (!promo.code) return false;
  if (!usedCode) return true;
  return promo.code.trim().toUpperCase() === usedCode.trim().toUpperCase();
}

/**
 * Dòng này đã được TÍNH VÀO `total_discount` chưa (bug #250).
 *
 * BE liệt kê cả khuyến mãi đủ điều kiện nhưng chưa thực áp, nên cộng tay
 * `discount_amount` của mọi dòng sẽ ra số khác ô "Discount" ngay bên trên —
 * đúng kiểu hai con số đá nhau trên cùng một màn hình mà BE vừa sửa ở phía họ.
 *
 * Thiếu cờ (QA chưa deploy) thì coi như ĐÃ áp: đó là hành vi của bản cũ, và
 * giả định ngược lại sẽ làm mọi dòng trông như chưa áp.
 */
function isApplied(promo: ApplicablePromotion): boolean {
  return promo.applied !== false;
}

/** Số về `null` khi không đọc được, để chỗ hiển thị tự quyết định hiện gì. */
function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function PromotionTesterModal({ defaultStoreId, onClose }: PromotionTesterModalProps) {
  const { t } = useI18n();
  const money = useMoney();
  const { mutate: verifyCart, data: result, isPending, reset } = useVerifyCart();

  // Nạp ngay trong modal: component chỉ mount khi người dùng mở màn thử, nên
  // trang Khuyến mãi không phải gánh hai request này ở mọi lượt vào.
  const { data: storesData } = useStores({ page_size: 100 });
  const { data: productsData } = useProducts({ page_size: 200 });
  /**
   * Danh sách khuyến mãi để dựng droplist mã (thay ô gõ tay).
   *
   * KHÔNG lọc theo trạng thái: màn này sinh ra để thử một khuyến mãi TRƯỚC KHI
   * bật nó, nên mã DRAFT phải chọn được — lọc chỉ lấy ACTIVE là bỏ đúng ca dùng
   * chính. Trạng thái hiện ngay trong nhãn để người thử biết mình đang thử cái
   * gì.
   */
  const { data: promotionsData } = usePromotions({ page_size: 200 } as never);

  const storeOptions = useMemo(
    () =>
      ((storesData as { results?: Array<{ id: string; name: string }> } | undefined)?.results ?? [])
        .map((s) => ({ value: s.id, label: s.name })),
    [storesData]
  );
  const productOptions = useMemo(
    () =>
      ((productsData as { results?: Array<{ id: string; name: string; sku?: string }> } | undefined)
        ?.results ?? []
      ).map((p) => ({ value: p.id, label: p.sku ? `${p.name} (${p.sku})` : p.name })),
    [productsData]
  );

  /**
   * Mã khuyến mãi có thể thử.
   *
   * Chỉ lấy bản ghi CÓ `code` — khuyến mãi tự động không có mã để nhập, chúng
   * vẫn được engine áp và hiện ở phần kết quả dưới nhãn "Tự động".
   *
   * Lựa chọn đầu là "không dùng mã": đó là một ca thử thật, không phải trạng
   * thái rỗng — nó trả lời câu "giỏ này được giảm bao nhiêu nếu khách không có
   * voucher nào". Chính câu hỏi đó đã sinh ra bug #250.
   */
  const promoOptions = useMemo(() => {
    const raw =
      (promotionsData as { results?: Array<Record<string, unknown>> } | undefined)?.results ?? [];
    const withCode = raw
      .map((row) => ({
        code: String(row.code ?? '').trim(),
        name: String(row.name ?? '').trim(),
        status: String(row.status ?? '').trim(),
      }))
      .filter((row) => row.code);

    // Trùng mã thì giữ bản đầu — danh sách chọn không nên có hai dòng y hệt.
    const seen = new Set<string>();
    const unique = withCode.filter((row) => {
      const key = row.code.toUpperCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return [
      { value: '', label: t('promoTesterNoCodeOption') },
      ...unique.map((row) => ({
        value: row.code,
        // Mã đứng trước vì đó là thứ người dùng tìm; tên là ngữ cảnh.
        //
        // Trạng thái CHỈ hiện khi khác ACTIVE. Gắn "(ACTIVE)" vào gần như mọi
        // dòng thì nó thành nhiễu — mắt học cách bỏ qua, và đúng lúc gặp
        // DRAFT/EXPIRED lại không nhận ra. Nhãn cũng ngắn lại nên phần lớn dòng
        // vừa đúng một hàng trong droplist.
        label: [
          row.code,
          row.name && `— ${row.name}`,
          row.status && row.status.toUpperCase() !== 'ACTIVE' && `(${row.status})`,
        ]
          .filter(Boolean)
          .join(' '),
      })),
    ];
  }, [promotionsData, t]);

  const [storeId, setStoreId] = useState(defaultStoreId ?? '');
  const [promoCode, setPromoCode] = useState('');
  /**
   * Đã tự chọn mã mặc định chưa.
   *
   * Cần cờ riêng chứ không kiểm `promoCode === ''`: chuỗi rỗng là một lựa chọn
   * HỢP LỆ ("không dùng mã"). Thiếu cờ này thì người dùng chọn "không dùng mã",
   * danh sách refetch một nhịp, và lựa chọn của họ bị ghi đè bằng mã đầu tiên.
   */
  const [codeAutoPicked, setCodeAutoPicked] = useState(false);

  // Mã đầu tiên trong danh sách được chọn sẵn để bấm Run test là chạy được
  // ngay, không phải đi tra mã ở màn khác rồi gõ lại.
  useEffect(() => {
    if (codeAutoPicked) return;
    const firstCode = promoOptions.find((o) => o.value)?.value;
    if (!firstCode) return;
    setPromoCode(firstCode);
    setCodeAutoPicked(true);
  }, [promoOptions, codeAutoPicked]);
  const [lines, setLines] = useState<CartLine[]>([{ productId: '', quantity: '1' }]);
  const [error, setError] = useState<string | null>(null);
  /** Mã đã gửi ở lần chạy gần nhất — xem ghi chú ở `promoCodeUsed` (bug #250). */
  const [lastRunCode, setLastRunCode] = useState<string | null>(null);

  const updateLine = (index: number, patch: Partial<CartLine>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const handleRun = () => {
    setError(null);
    reset();

    if (!storeId) {
      setError(t('promoTesterErrorNoStore'));
      return;
    }

    const items = lines
      .filter((l) => l.productId && Number(l.quantity) > 0)
      .map((l) => ({ product_id: l.productId, quantity: Number(l.quantity) }));

    if (items.length === 0) {
      setError(t('promoTesterErrorNoItems'));
      return;
    }

    const trimmedCode = promoCode.trim();
    setLastRunCode(trimmedCode || null);

    verifyCart(
      {
        store_id: storeId,
        items,
        ...(trimmedCode ? { promo_code: trimmedCode } : {}),
      },
      {
        onError: (err: unknown) => {
          setError(err instanceof Error ? err.message : t('promoTesterErrorGeneric'));
        },
      }
    );
  };

  const promotions = result?.applicable_promotions ?? [];
  /**
   * Mã đã thực sự GỬI LÊN ở lần chạy vừa rồi.
   *
   * Không đọc `promoCode` (state của ô nhập): người dùng có thể gõ thêm vào ô
   * sau khi bấm Run, và khi đó nhãn dưới kết quả sẽ nói về một mã chưa từng
   * được thử.
   */
  const promoCodeUsed = lastRunCode;
  const codePromotions = promotions.filter((p) => isCodePromotion(p, promoCodeUsed));
  const autoPromotions = promotions.filter((p) => !isCodePromotion(p, promoCodeUsed));
  /** Đủ điều kiện nhưng chưa nằm trong `total_discount`. */
  const pendingPromotions = promotions.filter((p) => !isApplied(p));
  const subtotal = toNumber(result?.subtotal);
  const totalDiscount = toNumber(result?.total_discount);
  const estimatedTotal = toNumber(result?.estimated_total);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('promoTesterTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">{t('promoTesterSubtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                {t('storeWarehouseName')} <span className="text-red-500">*</span>
              </label>
              <SelectDropdown
                options={storeOptions}
                value={storeId}
                onChange={setStoreId}
                placeholder={t('selectStore')}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                {t('promoTesterCodeLabel')}
              </label>
              {/* Droplist thay ô gõ tay: mã khuyến mãi là dữ liệu CÓ SẴN, bắt
                  người dùng nhớ và gõ lại đúng từng ký tự chỉ tạo ra lỗi gõ sai
                  rồi hiểu nhầm thành "engine không nhận mã". */}
              <SelectDropdown
                options={promoOptions}
                value={promoCode}
                onChange={(val) => {
                  setPromoCode(val);
                  // Người dùng đã tự chọn — đừng tự động ghi đè nữa.
                  setCodeAutoPicked(true);
                }}
                placeholder={t('promoTesterNoCodeOption')}
              />
              <p className="mt-1 text-xs text-slate-400">{t('promoTesterCodeHint')}</p>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              {t('promoTesterCartLabel')}
            </label>
            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="flex gap-2">
                  <div className="min-w-0 flex-1">
                    <SelectDropdown
                      options={productOptions}
                      value={line.productId}
                      onChange={(v) => updateLine(i, { productId: v })}
                      placeholder={t('selectProduct')}
                    />
                  </div>
                  <input
                    type="number"
                    min={1}
                    value={line.quantity}
                    onChange={(e) => updateLine(i, { quantity: e.target.value })}
                    className="h-10 w-20 shrink-0 rounded-lg border border-slate-300 px-2 text-right text-sm outline-none focus:border-primary-400"
                  />
                  <button
                    onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                    disabled={lines.length === 1}
                    className="h-10 shrink-0 rounded-lg px-3 text-sm text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-30"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setLines((prev) => [...prev, { productId: '', quantity: '1' }])}
              className="mt-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
            >
              + {t('promoTesterAddLine')}
            </button>
          </div>

          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          {/* Kết quả */}
          {result && !error && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              {/**
               * Bug #250 — "chưa nhập mã voucher mà vẫn ra tiền giảm".
               *
               * Engine chạy đúng: khuyến mãi TỰ ĐỘNG (giảm theo danh mục, theo
               * hạng khách, theo khung giờ…) áp dụng mà không cần mã nào cả —
               * đó là định nghĩa của chúng, và giỏ hàng thật ở POS cũng được
               * giảm y như vậy. Cái sai nằm ở màn hình: nó đổ chung mọi khuyến
               * mãi vào một danh sách, nên người thử đọc ra thành "hệ thống tự
               * tính giảm giá cho một voucher chưa được chọn".
               *
               * Tách hai nhóm và nói rõ nhóm nào là nhóm nào. Chặn bớt phần
               * giảm giá ở đây thì màn thử sẽ nói dối về số tiền thật khách
               * phải trả — tệ hơn nhiều so với một nhãn khó hiểu.
               */}
              {autoPromotions.length > 0 && !promoCodeUsed && (
                <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  {t('promoTesterAutoNotice')}
                </p>
              )}
              {/* Dòng đủ điều kiện nhưng CHƯA áp — thường là khuyến mãi cần mã
                  mà người dùng chưa nhập. Nói rõ để không ai cộng tay các số
                  bên dưới rồi thắc mắc vì sao lệch ô "Discount". */}
              {pendingPromotions.length > 0 && (
                <p className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600">
                  {t('promoTesterNotAppliedNotice', { count: pendingPromotions.length })}
                </p>
              )}
              {/* Nhập mã nhưng không mã nào trong kết quả khớp -> nói thẳng,
                  nếu không người dùng tưởng mã đã được áp. */}
              {promoCodeUsed && codePromotions.length === 0 && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {t('promoTesterCodeNoMatch', { code: promoCodeUsed })}
                </p>
              )}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('promoTesterSubtotal')}
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-900">
                    {subtotal !== null ? money.format(subtotal) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('promoTesterDiscount')}
                  </p>
                  <p className="mt-1 text-sm font-bold text-emerald-600">
                    {totalDiscount !== null ? `−${money.format(totalDiscount)}` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('promoTesterEstimatedTotal')}
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-900">
                    {estimatedTotal !== null ? money.format(estimatedTotal) : '—'}
                  </p>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-3">
                {promotions.length === 0 ? (
                  // Rỗng là câu trả lời hợp lệ, không phải sự cố — nói rõ để
                  // người dùng không đi tìm lỗi kỹ thuật không tồn tại.
                  <p className="text-sm text-slate-500">{t('promoTesterNoMatch')}</p>
                ) : (
                  <ul className="space-y-2">
                    {promotions.map((p, i) => {
                      const amount = toNumber(p.discount_amount);
                      return (
                        <li
                          key={p.id ?? i}
                          className="flex items-start justify-between gap-3 rounded-lg bg-white px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                              {p.name ?? p.code ?? '—'}
                              {/* Nhãn phân loại (bug #250) — nguồn duy nhất để
                                  biết dòng này có cần mã hay không. */}
                              {isCodePromotion(p, promoCodeUsed) ? (
                                <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-700">
                                  {t('promoTesterBadgeCode')}
                                </span>
                              ) : (
                                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                                  {t('promoTesterBadgeAuto')}
                                </span>
                              )}
                            </p>
                            {p.description && (
                              <p className="text-xs text-slate-500">{p.description}</p>
                            )}
                          </div>
                          {amount !== null && (
                            <span className="shrink-0 text-right">
                              {/* Dòng chưa áp: số tiền vẫn hiện (người dùng cần
                                  biết sẽ giảm bao nhiêu NẾU áp) nhưng làm mờ và
                                  ghi rõ là chưa tính vào tổng. */}
                              <span
                                className={`block text-sm font-bold ${
                                  isApplied(p) ? 'text-emerald-600' : 'text-slate-400'
                                }`}
                              >
                                −{money.format(amount)}
                              </span>
                              {!isApplied(p) && (
                                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                  {t('promoTesterNotApplied')}
                                </span>
                              )}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
          >
            {t('close')}
          </button>
          <button
            onClick={handleRun}
            disabled={isPending}
            className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending ? t('loading') : t('promoTesterRun')}
          </button>
        </div>
      </div>
    </div>
  );
}
